const BRIDGE_SOURCE = 'huay-firebase-auth-bridge'
const CLIENT_SOURCE = 'huay-firebase-auth-client'
const DEFAULT_BRIDGE_URL = 'https://otp.huaytded365day.com/firebase-auth-bridge'

type BridgeReadyMessage = { source: typeof BRIDGE_SOURCE; type: 'READY' }
type BridgeSentMessage = { source: typeof BRIDGE_SOURCE; type: 'PHONE_AUTH_SENT'; payload?: { verificationId?: string } }
type BridgeVerifiedMessage = { source: typeof BRIDGE_SOURCE; type: 'PHONE_AUTH_VERIFIED'; payload?: { uid?: string; phoneNumber?: string | null } }
type BridgeErrorMessage = { source: typeof BRIDGE_SOURCE; type: 'ERROR'; payload?: { message?: string; code?: string } }
type BridgeMessage = BridgeReadyMessage | BridgeSentMessage | BridgeVerifiedMessage | BridgeErrorMessage

interface SendOptions {
  tenantId: string
  phoneNumber: string
  apiBaseUrl?: string
  timeoutMs?: number
}

interface RedirectSendOptions extends SendOptions {
  returnUrl: string
}

interface RedirectVerifyOptions {
  flowId: string
  otpCode: string
  returnUrl: string
}

interface PersistedBridgeSession {
  tenantId: string
  phoneNumber: string
  apiBaseUrl: string
  verificationId: string
  createdAt: number
}

export interface LineBridgeFlowContext {
  flowId: string
  tenantId: string
  phoneNumber: string
  apiBaseUrl: string
  sessionId: string
  createdAt: number
}

export type FirebaseSmsBillingStatus = 'sent' | 'failed' | 'verified'

export interface ReportFirebaseSmsBillingOptions {
  apiBaseUrl: string
  tenantId: string
  phoneNumber: string
  status: FirebaseSmsBillingStatus
  messageType?: string
  source: string
}

let bridgeWindow: Window | null = null
let bridgeOrigin = ''
const BRIDGE_SESSION_STORAGE_KEY = 'huay-firebase-auth-bridge-session'
const LINE_BRIDGE_FLOW_STORAGE_KEY = 'huay-firebase-auth-bridge-line-flow'
const BRIDGE_SESSION_TTL_MS = 10 * 60 * 1000

function getBridgeUrl() {
  return ((import.meta.env as any).VITE_FIREBASE_AUTH_BRIDGE_URL as string) || DEFAULT_BRIDGE_URL
}

function loadPersistedBridgeSession(): PersistedBridgeSession | null {
  try {
    const raw = window.sessionStorage.getItem(BRIDGE_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedBridgeSession
    if (
      !parsed?.tenantId
      || !parsed?.phoneNumber
      || !parsed?.verificationId
      || !parsed?.createdAt
      || (Date.now() - parsed.createdAt) > BRIDGE_SESSION_TTL_MS
    ) {
      clearPersistedBridgeSession()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function savePersistedBridgeSession(session: PersistedBridgeSession) {
  try {
    window.sessionStorage.setItem(BRIDGE_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Ignore storage failures and rely on the live popup state.
  }
}

function clearPersistedBridgeSession() {
  try {
    window.sessionStorage.removeItem(BRIDGE_SESSION_STORAGE_KEY)
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function isLineInAppBrowser() {
  const userAgent = navigator.userAgent || ''
  return /Line\//i.test(userAgent) || /\bLIFF\b/i.test(userAgent)
}

/** In-app browsers that block popups or fail postMessage bridge (LINE, FB, IG, etc.) */
export function isRestrictedInAppBrowser() {
  const userAgent = navigator.userAgent || ''
  return (
    isLineInAppBrowser()
    || /FBAN|FBAV/i.test(userAgent)
    || /Instagram/i.test(userAgent)
    || /MicroMessenger/i.test(userAgent)
    || /Twitter/i.test(userAgent)
    || /KAKAOTALK/i.test(userAgent)
  )
}

export function isMobileDevice() {
  const userAgent = navigator.userAgent || ''
  return /iPhone|iPad|iPod|Android/i.test(userAgent)
}

export function shouldUseFirebaseAuthBridge() {
  const enabled = ((import.meta.env as any).VITE_ENABLE_FIREBASE_AUTH_BRIDGE ?? 'true') === 'true'
  const bridgeUrl = new URL(getBridgeUrl())
  return enabled && window.location.origin !== bridgeUrl.origin
}

export function shouldUseFirebaseAuthBridgeRedirect() {
  const redirectEnabled = ((import.meta.env as any).VITE_LINE_OTP_REDIRECT ?? 'true') === 'true'
  const mobileRedirectEnabled = ((import.meta.env as any).VITE_MOBILE_OTP_REDIRECT ?? 'true') === 'true'
  if (!shouldUseFirebaseAuthBridge() || !redirectEnabled) {
    return false
  }
  return isRestrictedInAppBrowser() || (mobileRedirectEnabled && isMobileDevice())
}

export function saveLineBridgeFlowContext(context: LineBridgeFlowContext) {
  try {
    window.sessionStorage.setItem(LINE_BRIDGE_FLOW_STORAGE_KEY, JSON.stringify(context))
  } catch {
    // Ignore storage failures.
  }
}

export function loadLineBridgeFlowContext(): LineBridgeFlowContext | null {
  try {
    const raw = window.sessionStorage.getItem(LINE_BRIDGE_FLOW_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LineBridgeFlowContext
    if (
      !parsed?.flowId
      || !parsed?.tenantId
      || !parsed?.phoneNumber
      || !parsed?.sessionId
      || !parsed?.createdAt
      || (Date.now() - parsed.createdAt) > BRIDGE_SESSION_TTL_MS
    ) {
      clearLineBridgeFlowContext()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearLineBridgeFlowContext() {
  try {
    window.sessionStorage.removeItem(LINE_BRIDGE_FLOW_STORAGE_KEY)
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function buildFirebaseAuthBridgeRedirectSendUrl(options: RedirectSendOptions & { flowId: string }) {
  const bridgeUrl = new URL(getBridgeUrl())
  bridgeUrl.searchParams.set('mode', 'redirect')
  bridgeUrl.searchParams.set('action', 'send')
  bridgeUrl.searchParams.set('flowId', options.flowId)
  bridgeUrl.searchParams.set('tenantId', options.tenantId)
  bridgeUrl.searchParams.set('phoneNumber', options.phoneNumber)
  bridgeUrl.searchParams.set('apiBaseUrl', options.apiBaseUrl || '')
  bridgeUrl.searchParams.set('origin', window.location.origin)
  bridgeUrl.searchParams.set('returnUrl', options.returnUrl)
  return bridgeUrl.toString()
}

export function buildFirebaseAuthBridgeRedirectVerifyUrl(options: RedirectVerifyOptions) {
  const bridgeUrl = new URL(getBridgeUrl())
  bridgeUrl.searchParams.set('mode', 'redirect')
  bridgeUrl.searchParams.set('action', 'verify')
  bridgeUrl.searchParams.set('flowId', options.flowId)
  bridgeUrl.searchParams.set('origin', window.location.origin)
  bridgeUrl.searchParams.set('returnUrl', options.returnUrl)
  bridgeUrl.hash = `otp=${encodeURIComponent(options.otpCode)}`
  return bridgeUrl.toString()
}

export function redirectToFirebaseAuthBridgeSend(options: RedirectSendOptions, flowId = crypto.randomUUID()) {
  window.location.assign(buildFirebaseAuthBridgeRedirectSendUrl({ ...options, flowId }))
  return flowId
}

export function redirectToFirebaseAuthBridgeVerify(options: RedirectVerifyOptions) {
  window.location.assign(buildFirebaseAuthBridgeRedirectVerifyUrl(options))
}

/**
 * บันทึกค่าใช้จ่าย SMS OTP (Firebase) — endpoint เดิมกับ popup bridge
 * → sms-service firebase-auth-callback → SmsUsageRecord / Billing Summary
 */
export async function reportFirebaseSmsBilling(options: ReportFirebaseSmsBillingOptions) {
  try {
    const response = await fetch(`${options.apiBaseUrl}/api/v1/sms-service/sms-callbacks/firebase-auth-callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Tenant-ID': options.tenantId,
        'X-User-Role': 'anonymous',
        'X-Source': options.source,
      },
      credentials: 'include',
      body: JSON.stringify({
        phoneNumber: options.phoneNumber,
        status: options.status,
        tenantId: options.tenantId,
        provider: 'firebase',
        messageType: options.messageType || 'registration',
        clientInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screen: `${screen.width}x${screen.height}`,
          timestamp: new Date().toISOString(),
          source: options.source,
        },
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      console.warn('Firebase SMS billing callback failed:', response.status, data)
    }
  } catch (error) {
    console.warn('Firebase SMS billing callback error:', error)
  }
}

export function primeFirebaseAuthBridgeWindow() {
  const width = 460
  const height = 720
  const left = Math.max(window.screenX + Math.round((window.outerWidth - width) / 2), 0)
  const top = Math.max(window.screenY + Math.round((window.outerHeight - height) / 2), 0)

  bridgeWindow = window.open(
    '',
    'huay-firebase-auth-bridge',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  )

  if (!bridgeWindow) {
    throw new Error('Firebase auth popup was blocked')
  }

  try {
    bridgeWindow.document.title = 'Loading OTP bridge...'
  } catch {
    // Ignore cross-origin access errors if popup already navigated.
  }

  return bridgeWindow
}

function openBridgeWindow() {
  const bridgeUrl = new URL(getBridgeUrl())
  bridgeUrl.searchParams.set('origin', window.location.origin)
  bridgeOrigin = bridgeUrl.origin

  const popup = (bridgeWindow && !bridgeWindow.closed) ? bridgeWindow : primeFirebaseAuthBridgeWindow()
  popup.location.replace(bridgeUrl.toString())
  bridgeWindow = popup

  return bridgeWindow
}

function waitForBridgeMessage<T extends BridgeMessage['type']>(
  expectedType: T,
  timeoutMs: number,
): Promise<Extract<BridgeMessage, { type: T }>> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup()
      reject(new Error(`Firebase auth bridge timeout while waiting for ${expectedType}`))
    }, timeoutMs)

    const closeWatcher = window.setInterval(() => {
      if (bridgeWindow && bridgeWindow.closed) {
        cleanup()
        reject(new Error('Firebase auth popup was closed before completion'))
      }
    }, 500)

    const onMessage = (event: MessageEvent<BridgeMessage>) => {
      if (event.origin !== bridgeOrigin) {
        return
      }

      const message = event.data
      if (!message || message.source !== BRIDGE_SOURCE) {
        return
      }

      if (message.type === 'ERROR') {
        cleanup()
        reject(new Error(message.payload?.message || 'Firebase auth bridge failed'))
        return
      }

      if (message.type === expectedType) {
        cleanup()
        resolve(message as Extract<BridgeMessage, { type: T }>)
      }
    }

    const cleanup = () => {
      window.clearTimeout(timer)
      window.clearInterval(closeWatcher)
      window.removeEventListener('message', onMessage)
    }

    window.addEventListener('message', onMessage)
  })
}

export async function sendOTPViaFirebaseAuthBridge(options: SendOptions) {
  const popup = openBridgeWindow()

  await waitForBridgeMessage('READY', options.timeoutMs ?? 15000)

  popup.postMessage({
    source: CLIENT_SOURCE,
    type: 'START_PHONE_AUTH',
    payload: {
      tenantId: options.tenantId,
      phoneNumber: options.phoneNumber,
      apiBaseUrl: options.apiBaseUrl || '',
    },
  }, bridgeOrigin)

  const result = await waitForBridgeMessage('PHONE_AUTH_SENT', options.timeoutMs ?? 60000)
  const verificationId = result.payload?.verificationId

  if (verificationId) {
    savePersistedBridgeSession({
      tenantId: options.tenantId,
      phoneNumber: options.phoneNumber,
      apiBaseUrl: options.apiBaseUrl || '',
      verificationId,
      createdAt: Date.now(),
    })

    window.setTimeout(() => {
      closeFirebaseAuthBridge()
    }, 250)
  }

  return result
}

export async function verifyOTPViaFirebaseAuthBridge(otpCode: string, timeoutMs = 30000) {
  const persistedSession = loadPersistedBridgeSession()
  const popup = (bridgeWindow && !bridgeWindow.closed) ? bridgeWindow : openBridgeWindow()

  await waitForBridgeMessage('READY', 15000)

  popup.postMessage({
    source: CLIENT_SOURCE,
    type: 'VERIFY_PHONE_OTP',
    payload: {
      otpCode,
      verificationId: persistedSession?.verificationId,
      tenantId: persistedSession?.tenantId,
      apiBaseUrl: persistedSession?.apiBaseUrl || '',
    },
  }, bridgeOrigin)

  const result = await waitForBridgeMessage('PHONE_AUTH_VERIFIED', timeoutMs)
  clearPersistedBridgeSession()
  return result
}

export function closeFirebaseAuthBridge() {
  if (bridgeWindow && !bridgeWindow.closed) {
    bridgeWindow.postMessage({ source: CLIENT_SOURCE, type: 'CLOSE' }, bridgeOrigin)
    bridgeWindow.close()
  }

  bridgeWindow = null
}

export const FIREBASE_AUTH_BRIDGE_LINE_METHOD = 'firebase-auth-bridge-line'
